import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface InvoiceTransactionItem {
    itemCode?: string | null;
    name: string;
    productId?: number | null;
    description?: string | null;
    unitPrice: number;
    originalPrice?: number | null;
    discount?: number | null;
    discountAmount?: number | null;
    qty: number;
    amount: number;
}

export interface InvoicePaymentAllocation {
    paymode: string;
    amount: number;
    cardTypeCode?: string;
    cardStationNo?: string;
    cardApprovalCode?: string;
    cardLastDigits?: string;
}

export interface InvoicePromotionAllocation {
    promoId: number;
    promoCode?: string | null;
    promoName?: string | null;
    promoDisper?: number | null;
    promoDisamt?: number | null;
    promoFreeItem?: string | null;
    promoFreeVoucher?: string | null;
    promoDiscountAmount?: number | null;
    offerQty?: number | null;
}

export interface InvoiceTransactionRequest {
    customerId?: number | null;
    customerCode?: string | null;
    customerName?: string | null;
    salesRepId?: string | null;
    cashierId?: string | null;
    invoiceDate?: string | null;
    paymentReceived?: number;
    billDiscountPercentage?: number;
    billDiscountAmount?: number;
    items: InvoiceTransactionItem[];
    paymode?: string | null;
    payments?: InvoicePaymentAllocation[];
    promotions?: InvoicePromotionAllocation[];
    note?: string | null;
    /** VAT/tax percentage applied to this invoice; null/omitted for a normal (non-tax) invoice. */
    taxPer?: number | null;
}

export interface InvoiceTransactionResponse {
    invoice_no: string;
    item_count: number;
    amount: number;
    customer_code?: string | null;
    customer_name?: string | null;
    created_at?: string | null;
    status?: string;
    message?: string;
}

export interface RecentInvoiceResponse {
    invoice_no: string;
    item_count: number;
    amount: number;
    customer_name?: string | null;
    created_at?: string | null;
    items: any[];
    tax_per?: number | null;
}

export interface InvoicePaymentDetailResponse {
    paymode: string;
    paytypedesc?: string | null;
    amount: number;
    is_credit?: boolean;
    crdcardno?: string | null;
}

export interface InvoiceDetailResponse {
    invoice_no: string;
    customer_id?: number | null;
    customer_code?: string | null;
    customer_name?: string | null;
    customer_address?: string | null;
    customer_tin_no?: string | null;
    cashier_id?: string | null;
    created_at?: string | null;
    gross_amount?: number;
    bill_discount_percentage?: number;
    bill_discount_amount?: number;
    net_amount?: number;
    total_paid?: number;
    change_amount?: number;
    refno?: string | null;
    points_added?: number | null;
    customer_total_points?: number | null;
    promo_free_item?: string | null;
    promotion_discount_amount?: number;
    promo_free_voucher?: string | null;
    promotions?: any[];
    tax_per?: number | null;
    items: Array<{
        item_id?: number | null;
        item_code?: string | null;
        item_name?: string | null;
        name?: string | null;
        quantity: number;
        unit_price: number;
        original_price?: number | null;
        discount?: number | null;
        discount_amount?: number | null;
        amount: number;
        total?: number;
        sprice?: number | null;
        qty?: number | null;
        linedisval?: number | null;
        linedisamt?: number | null;
    }>;
    payments: InvoicePaymentDetailResponse[];
}

export const processInvoice = async (
    payload: InvoiceTransactionRequest
): Promise<InvoiceTransactionResponse> => {
    try {
        const response: ApiResponse<InvoiceTransactionResponse | null> = (
            await apiClient.post("/pos_invoice/create", {
                customer_id: payload.customerId ?? null,
                customer_code: payload.customerCode ?? null,
                customer_name: payload.customerName ?? null,
                sales_rep_id: payload.salesRepId ?? null,
                cashier_id: payload.cashierId ?? null,
                invoice_date: payload.invoiceDate ?? null,
                payment_received: payload.paymentReceived ?? 0,
                bill_discount_percentage: payload.billDiscountPercentage ?? 0,
                bill_discount_amount: payload.billDiscountAmount ?? 0,
                items: payload.items.map((item) => ({
                    item_id: item.productId ?? null,
                    item_code: item.itemCode ?? null,
                    item_name: item.name,
                    quantity: item.qty,
                    unit_price: item.unitPrice,
                    original_price: item.originalPrice ?? item.unitPrice,
                    discount: item.discount ?? 0,
                    discount_amount: item.discountAmount ?? 0,
                    amount: item.amount,
                })),
                paymode: payload.paymode ?? "CSH",
                payments: (payload.payments || []).map((payment) => ({
                    paymode: payment.paymode,
                    amount: payment.amount,
                    card_type_code: payment.cardTypeCode ?? null,
                    card_station_no: payment.cardStationNo ?? null,
                    card_approval_code: payment.cardApprovalCode ?? null,
                    card_last_digits: payment.cardLastDigits ?? null,
                })),
                promotions: (payload.promotions || []).map((promotion) => ({
                    promo_id: promotion.promoId,
                    promo_code: promotion.promoCode ?? null,
                    promo_name: promotion.promoName ?? null,
                    promo_disper: promotion.promoDisper ?? null,
                    promo_disamt: promotion.promoDisamt ?? null,
                    promo_free_item: promotion.promoFreeItem ?? null,
                    promo_free_voucher: promotion.promoFreeVoucher ?? null,
                    promo_discount_amount: promotion.promoDiscountAmount ?? null,
                    offer_qty: promotion.offerQty ?? null,
                })),
                note: payload.note ?? null,
                tax_per: payload.taxPer ?? null,
            }, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Invoice request was accepted without a response payload");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            const message = typeof detail === "string" ? detail : JSON.stringify(detail);
            throw new Error(message);
        }

        throw new Error("Failed to submit invoice");
    }
};

export const getRecentInvoices = async (
    limit: number = 10,
    search?: string,
    fromDate?: string,
    toDate?: string,
    offset: number = 0,
): Promise<RecentInvoiceResponse[]> => {
    try {
        const params: Record<string, any> = { limit, offset };
        if (search) {
            params.search = search;
        }
        if (fromDate) {
            params.from_date = fromDate;
        }
        if (toDate) {
            params.to_date = toDate;
        }

        const response: ApiResponse<RecentInvoiceResponse[]> = (
            await apiClient.get("/pos_invoice/recent", {
                params,
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        throw new Error("Failed to fetch recent invoices");
    }
};

export const countRecentInvoices = async (
    search?: string,
    fromDate?: string,
    toDate?: string,
): Promise<number> => {
    try {
        const params: Record<string, any> = {};
        if (search) {
            params.search = search;
        }
        if (fromDate) {
            params.from_date = fromDate;
        }
        if (toDate) {
            params.to_date = toDate;
        }

        const response: ApiResponse<number> = (
            await apiClient.get("/pos_invoice/recent/count", {
                params,
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || 0;
    } catch (error: any) {
        throw new Error("Failed to fetch receipt count");
    }
};

export const getInvoiceDetail = async (invoiceNo: string): Promise<InvoiceDetailResponse> => {
    try {
        const response: ApiResponse<InvoiceDetailResponse> = (
            await apiClient.get(`/pos_invoice/${encodeURIComponent(invoiceNo)}`, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        if (!response.data) {
            throw new Error("Invoice request completed without a response payload.");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            const detail = error.response.data.detail;
            throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }

        throw new Error("Failed to fetch invoice detail");
    }
};
